import { navigationCache, navigationCacheEnabled } from './lib/navigation-cache';
import {TrainingPlanScreen} from './screens/TrainingPlan';
import { useFoodScrollRestoration } from './lib/food-origin';
import { useEffect } from 'react';
import { localNow, mealByTime } from '@jungles/shared';
import { botIntents } from './lib/bot-intent';
import { NotificationsScreen } from './screens/Notifications';
import { useStartupReveal } from './lib/startup';
import { watchKeyboard } from './lib/keyboard';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from './app/session';
import { LoginScreen } from './screens/Login';
import { OnboardingScreen } from './screens/Onboarding';
import { HomeScreen } from './screens/Home';
import { WorkoutsScreen } from './screens/Workouts';
import { WorkoutScreen } from './screens/Workout';
import { TemplatesScreen } from './screens/Templates';
import { HistoryScreen } from './screens/History';
import { NutritionScreen } from './screens/Nutrition';
import { FoodSearchScreen } from './screens/FoodSearch';
import { FoodEditScreen } from './screens/FoodEdit';
import { FoodFormScreen } from './screens/FoodForm';
import { ScanScreen } from './screens/Scan';
import { LibraryScreen } from './screens/Library';
import { ArenaScreen } from './screens/Arena';
import { LeagueScreen } from './screens/League';
import { RulesScreen } from './screens/Rules';
import { ProfileScreen } from './screens/Profile';
import { PrivacyScreen } from './screens/Privacy';

export function App() {
  useEffect(watchKeyboard, []);
  useEffect(() => {
    if (!navigationCacheEnabled) return;
    const timer = window.setInterval(() => { if(document.visibilityState==='visible') navigationCache.tick(); }, 1000);
    return () => window.clearInterval(timer);
  }, []);
  useFoodScrollRestoration();
  const { status, user, botLaunchReady } = useSession();
  const location = useLocation();

  const navigate = useNavigate();
  const pending = user && botLaunchReady ? botIntents.pending(user.id) : null;
  useEffect(() => {
    if (!user?.onboarded || !botLaunchReady || !pending) return;
    const intent = botIntents.consume(user.id);
    if (!intent) return;
    const paths: Record<string, string> = { home: '/', nutrition: '/nutrition', results: '/arena', league: '/arena/league', notifications: '/profile/notifications', workout: '/workouts' };
    let to = paths[intent];
    if (intent === 'food') {
      // Use saved user timezone at dispatch, including after a long onboarding.
      try {
        const { date, time } = localNow(user.timezone);
        to = `/nutrition/food?date=${date}&meal=${mealByTime(time)}`;
      } catch { to = '/nutrition'; } // No guessed timezone or premature record.
    } else if (intent.startsWith('day_')) to = `/nutrition?date=${intent.slice(4)}`;
    navigate(to ?? '/', { replace: true, state: intent === 'workout' ? { botWorkout: true } : null });
  }, [user, botLaunchReady, pending, navigate]);
  const routingIntent = !!user?.onboarded && !!pending;
  useStartupReveal(status !== 'loading' && botLaunchReady && !routingIntent, !!user?.onboarded && location.pathname === '/', location.pathname);

  if (status === 'loading' || !botLaunchReady || routingIntent) return null;

  if (status === 'anonymous' || !user) {
    return (
      <Routes>
        <Route path="*" element={<LoginScreen />} />
      </Routes>
    );
  }

  // Новый аккаунт сначала проходит знакомство (SPEC §4).
  if (!user.onboarded && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  const isFoodSearch = location.pathname === '/nutrition/search';
  const background = (location.state as {foodBackground?:typeof location}|null)?.foodBackground;
  const safeBackground = background && ['/', '/nutrition'].includes(background.pathname) ? background : undefined;
  const diaryLocation = safeBackground ?? {...location,pathname:'/nutrition',search:`?date=${new URLSearchParams(location.search).get('date') ?? ''}`,state:null};
  return (
    <>
    <div data-food-background>
    <Routes location={isFoodSearch ? diaryLocation : location}>
      <Route path="/onboarding" element={<OnboardingScreen />} />
      <Route path="/" element={<HomeScreen />} />

      <Route path="/workouts/plan" element={<TrainingPlanScreen />} />
      <Route path="/workouts" element={<WorkoutsScreen />} />
      <Route path="/workouts/templates" element={<TemplatesScreen />} />
      <Route path="/workouts/history" element={<HistoryScreen />} />
      <Route path="/workouts/:id" element={<WorkoutScreen />} />

      <Route path="/nutrition" element={<NutritionScreen />} />
      <Route path="/nutrition/search" element={<FoodSearchScreen />} />
      <Route path="/nutrition/food" element={<FoodFormScreen />} />
      <Route path="/nutrition/scan" element={<ScanScreen />} />
      <Route path="/nutrition/library/:id/edit" element={<FoodEditScreen />} />
      <Route path="/nutrition/library" element={<LibraryScreen />} />

      <Route path="/arena" element={<ArenaScreen />} />
      <Route path="/arena/league" element={<LeagueScreen kind="personal" />} />
      <Route path="/arena/gym" element={<LeagueScreen kind="gym" />} />
      <Route path="/arena/rules" element={<RulesScreen />} />

      <Route path="/profile" element={<ProfileScreen />} />
      <Route path="/profile/notifications" element={<NotificationsScreen key={user.id} />} />
      <Route path="/profile/privacy" element={<PrivacyScreen />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </div>
    {isFoodSearch ? <FoodSearchScreen /> : null}
    </>
  );
}
